import { Engineering, PinDrop } from '@mui/icons-material';
import {
    Avatar,
    Box,
    Button,
    Card,
    CardContent,
    Chip,
    Collapse,
    Grid,
    List,
    ListItem,
    ListItemText,
    Stack,
    Typography,
} from '@mui/material';
import { startOfDay } from 'date-fns';
import React from 'react';
import { FieldValues, FormProvider, useForm, useWatch } from 'react-hook-form';
import {
    useEditOwnerContractorMutation,
    useGetPricingTemplatesQuery,
} from '../../../../app/Slices/SevenDayApiSlice';
import { CustomSmallListChip } from '../../../../layout/styledcomponents/CustomChip';
import UserRole from '../../../../shared/AuthRoles';
import {
    camelCaseToTitleCase,
    capitalizeName,
    formatTime,
} from '../../../../shared/utils';
import FormSelect from '../../../shared/FormSelect';
import toast from '../../../shared/Toast';
import { OwnerContractor, Period } from '../../../types/contractor';
import PricingTemplatePreview from '../PricingTemplates/PricingTemplatePreview';

interface ContractorProfileProps {
    contractor: OwnerContractor;
}

const ContractorProfile = ({ contractor }: ContractorProfileProps) => {
    const loggedInUser = UserRole();

    const { data: templates } = useGetPricingTemplatesQuery(contractor.id);
    const [updateContractor] = useEditOwnerContractorMutation();

    const methods = useForm({ defaultValues: contractor });

    const {
        control,
        formState: { isDirty },
        reset,
        handleSubmit,
    } = methods;

    const availableDays = useWatch({ name: 'availableDays', control });
    const unavailablePeriods = useWatch({
        name: 'unavailablePeriods',
        control,
    }).filter((p: Period) => new Date(p.to) >= startOfDay(new Date()));

    const onSubmit = async (data: FieldValues) => {
        const request = {
            contractorId: contractor.id,
            permissions: data.permissions,
        };
        try {
            if (!loggedInUser?.company) throw new Error();

            const contractor = await updateContractor([
                loggedInUser.company,
                request,
            ]).unwrap();
            reset(contractor);

            toast.success('Contractor permissions saved successfully!');
        } catch (error) {
            toast.error('Error updating contractor permissions');
        }
    };

    return (
        <FormProvider {...methods}>
            <form onSubmit={handleSubmit(onSubmit)}>
                <Stack spacing={4} sx={{ pt: '30px' }}>
                    <Card>
                        <CardContent>
                            <Stack spacing={2}>
                                <Box
                                    sx={{
                                        display: 'flex',
                                        gap: 2,
                                        alignItems: 'center',
                                    }}
                                >
                                    <Avatar>
                                        <Engineering />
                                    </Avatar>
                                    <div>
                                        <Typography sx={{ fontWeight: 'bold' }}>
                                            {contractor.name} -{' '}
                                            <CustomSmallListChip
                                                size='small'
                                                color='green'
                                                label={contractor.services.type.toUpperCase()}
                                            />
                                        </Typography>
                                        <Typography>
                                            ABN: {contractor.abn}
                                        </Typography>
                                    </div>
                                </Box>
                                <Box
                                    sx={{
                                        display: 'flex',
                                        flexWrap: 'wrap',
                                        gap: 0.5,
                                    }}
                                >
                                    {contractor.services.items.map((item) => (
                                        <Chip
                                            key={item}
                                            label={
                                                item === 'bbqCleaning'
                                                    ? 'BBQ Cleaning'
                                                    : camelCaseToTitleCase(item)
                                            }
                                        />
                                    ))}
                                </Box>
                            </Stack>
                        </CardContent>
                    </Card>
                    <div>
                        <Typography variant='subHeading'>
                            Contact Details
                        </Typography>
                        <Grid
                            container
                            sx={{ pt: 2, rowGap: 1, color: 'text.primary' }}
                        >
                            <Grid item xs={12} md={6} lg={3}>
                                <Typography sx={{ fontWeight: 'bold' }}>
                                    Primary Contact
                                </Typography>
                                <Typography>
                                    {contractor.primaryContact}
                                </Typography>
                            </Grid>
                            <Grid item xs={12} md={6} lg={3}>
                                <Typography sx={{ fontWeight: 'bold' }}>
                                    Email
                                </Typography>
                                <Typography>{contractor.email}</Typography>
                            </Grid>
                            <Grid item xs={12} md={6} lg={3}>
                                <Typography sx={{ fontWeight: 'bold' }}>
                                    Phone
                                </Typography>
                                <Typography>{contractor.phone}</Typography>
                            </Grid>
                            <Grid item xs={12} md={6} lg={3}>
                                <Typography sx={{ fontWeight: 'bold' }}>
                                    Address
                                </Typography>
                                <Typography>
                                    {contractor.streetAddress},{' '}
                                    {contractor.suburb}, {contractor.state},{' '}
                                    {contractor.postcode}
                                </Typography>
                            </Grid>
                        </Grid>
                    </div>
                    <div>
                        <Typography variant='subHeading'>
                            Permissions
                        </Typography>
                        <FormSelect
                            name='permissions'
                            control={control}
                            options={[
                                {
                                    value: 'disabled',
                                    label: 'Disabled',
                                },
                                {
                                    value: 'readAndWrite',
                                    label: 'Read and Write',
                                },
                                {
                                    value: 'readOnly',
                                    label: 'Read Only',
                                },
                            ]}
                        />
                        <Collapse in={isDirty} unmountOnExit>
                            <Box
                                sx={{
                                    display: 'flex',
                                    justifyContent: 'flex-end',
                                    gap: 1,
                                    pt: 1,
                                }}
                            >
                                <Button
                                    variant='contained'
                                    color='error'
                                    onClick={() => reset(contractor)}
                                >
                                    Cancel
                                </Button>
                                <Button variant='contained' type='submit'>
                                    Save
                                </Button>
                            </Box>
                        </Collapse>
                    </div>
                    <div>
                        <Typography variant='subHeading'>
                            Service States
                        </Typography>
                        <List>
                            {contractor.serviceStates.map(
                                ({ state, areas }) => (
                                    <ListItem divider>
                                        <Stack spacing={1}>
                                            <Typography
                                                sx={{
                                                    fontWeight: 'bold',
                                                    color: 'text.primary',
                                                }}
                                            >
                                                <PinDrop /> {state}
                                            </Typography>
                                            <div>
                                                {areas.map(({ name }) => (
                                                    <Typography
                                                        sx={{
                                                            color: 'text.primary',
                                                        }}
                                                    >
                                                        - {name}
                                                    </Typography>
                                                ))}
                                            </div>
                                        </Stack>
                                    </ListItem>
                                ),
                            )}
                        </List>
                    </div>

                    <Stack>
                        <Typography variant='subHeading'>
                            Availability
                        </Typography>
                        <List
                            sx={{
                                listStyleType: 'disc',
                                pl: unavailablePeriods?.length > 0 ? 3 : 0,
                            }}
                        >
                            {availableDays.length > 0 && (
                                <ListItem
                                    disablePadding
                                    sx={{
                                        display:
                                            unavailablePeriods?.length > 0
                                                ? 'list-item'
                                                : 'default',
                                    }}
                                >
                                    <ListItemText primary='This contractor accepts jobs on the following days:' />
                                    <Stack spacing={1}>
                                        {availableDays.map(
                                            ({ day, periods }) => (
                                                <div key={day}>
                                                    <Typography fontWeight='bold'>
                                                        {capitalizeName(day)}
                                                    </Typography>
                                                    <Typography>
                                                        {periods.length === 0
                                                            ? 'All day'
                                                            : periods
                                                                  .map(
                                                                      ({
                                                                          from,
                                                                          to,
                                                                      }) =>
                                                                          `${formatTime(from)} - ${formatTime(to)}`,
                                                                  )
                                                                  .join(', ')}
                                                    </Typography>
                                                </div>
                                            ),
                                        )}
                                    </Stack>
                                </ListItem>
                            )}
                            {unavailablePeriods?.length > 0 && (
                                <ListItem
                                    disablePadding
                                    sx={{ display: 'list-item' }}
                                >
                                    <ListItemText
                                        primary={`This contractor is unavailable on the following periods:
                                      `}
                                        secondary={
                                            <>
                                                {unavailablePeriods.map(
                                                    (p: Period, index) => (
                                                        <Typography key={index}>
                                                            {p.from} → {p.to}
                                                        </Typography>
                                                    ),
                                                )}
                                            </>
                                        }
                                    />
                                </ListItem>
                            )}
                        </List>
                    </Stack>
                    {contractor.cancellationPolicy && (
                        <Stack gap={1}>
                            <Typography variant='subHeading'>
                                Cancellation Policy
                            </Typography>
                            <Typography variant='caption' fontWeight={550}>
                                {contractor.cancellationPolicy}
                            </Typography>
                        </Stack>
                    )}

                    {templates && templates?.length > 0 && (
                        <div>
                            <Typography variant='subHeading'>
                                Pricing
                            </Typography>
                            {templates.map((template) => (
                                <PricingTemplatePreview template={template} />
                            ))}
                        </div>
                    )}
                </Stack>
            </form>
        </FormProvider>
    );
};

export default ContractorProfile;
